import { Fancybox as NativeFancybox } from '@fancyapps/ui'
import { useRef, useEffect, useState } from 'react'
import '@fancyapps/ui/dist/fancybox/fancybox.css'
import { getCookiesValue } from 'helpers/cookie'
import { formatDate } from 'helpers/formatDate'
import Icon from 'ui/components/Icon'
import {
  FileCard,
  FileDelete,
  FileInfo,
  FileTitle,
  FileImage,
  ButtonWrapper,
  FileIconWrap,
  FileIcon,
} from './styled'

export const LightBoxFileList = ({
  images,
  deleteFiles = null,
  options = {},
  delegate = '[data-fancybox]',
}) => {
  const containerRef = useRef(null)
  const objectUrlsRef = useRef([])
  const [previewMap, setPreviewMap] = useState({})

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const mergedOptions = {
      Carousel: { infinite: true },
      Images: {
        zoom: true,
        Panzoom: {
          maxScale: 3,
        },
      },
      contentClick: 'iterateZoom',
      wheel: 'zoom',
      dragToClose: false,
      Toolbar: {
        display: {
          left: [],
          middle: ['zoomIn', 'zoomOut', 'toggle1to1'],
          right: ['close'],
        },
      },
      ...options,
    }

    NativeFancybox.bind(container, delegate, mergedOptions)

    return () => {
      try {
        NativeFancybox.unbind(container)
        NativeFancybox.close()
      } catch {}
    }
  }, [delegate])

  useEffect(() => {
    if (!images?.length) {
      objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url))
      objectUrlsRef.current = []
      setPreviewMap({})
      return
    }

    const token = getCookiesValue('ac_token')
    let cancelled = false

    const loadImages = async () => {
      const nextObjectUrls = []
      const results = await Promise.all(
        images.map(async (image) => {
          if (!image?.id || !image?.link || !image.is_api) {
            return [image?.id, image?.link || null]
          }

          try {
            const response = await fetch(image.link, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            })

            if (!response.ok) {
              return [image.id, null]
            }

            const blob = await response.blob()
            const objectUrl = URL.createObjectURL(blob)
            nextObjectUrls.push(objectUrl)

            return [image.id, objectUrl]
          } catch {
            return [image.id, null]
          }
        })
      )

      if (cancelled) {
        nextObjectUrls.forEach((url) => URL.revokeObjectURL(url))
        return
      }

      objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url))
      objectUrlsRef.current = nextObjectUrls

      setPreviewMap(Object.fromEntries(results))
    }

    loadImages()

    return () => {
      cancelled = true
    }
  }, [images])

  useEffect(() => {
    return () => {
      objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url))
      objectUrlsRef.current = []
    }
  }, [])

  return (
    <div ref={containerRef} className="lightbox-file-list">
      {images?.map((image) => {
        const previewUrl = previewMap[image.id]
        const hasImage = Boolean(previewUrl)

        return (
          <div key={image.id} className="lightbox-file-list__wrapper">
            <ButtonWrapper>
              {deleteFiles && (
                <FileDelete
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    deleteFiles(image.id)
                  }}
                >
                  <Icon name="trash" size="16" />
                </FileDelete>
              )}
            </ButtonWrapper>

            <FileCard
              as={hasImage ? 'a' : 'div'}
              {...(hasImage
                ? {
                    'data-fancybox': 'gallery',
                    'data-src': previewUrl,
                    href: previewUrl,
                  }
                : {})}
              aria-disabled={!hasImage}
              style={{ cursor: hasImage ? 'pointer' : 'default' }}
            >
              <FileImage>
                {hasImage ? (
                  <img src={previewUrl} alt={image.original_name} />
                ) : (
                  <FileIconWrap>
                    <FileIcon>
                      <use xlinkHref="/sprites/general.svg#file-icon" />
                    </FileIcon>
                  </FileIconWrap>
                )}
              </FileImage>

              <FileInfo>
                <FileTitle title={image.original_name}>
                  {image.original_name}
                </FileTitle>
                <div>{formatDate(image.created_at)}</div>
              </FileInfo>
            </FileCard>
          </div>
        )
      })}
    </div>
  )
}
